import { reactive, shallowRef, toRefs, Ref } from "vue";
import type { AutocompletionRequest } from "./types";
import autocompletionRequestBuilder from "./helpers/autocompletionRequestBuilder";
import { watchDebounced } from "@vueuse/core";

export interface GooglePlacesAutocompleteOptions {
  autocompletionRequest?: AutocompletionRequest;
  debounce?: number;
  minLengthAutocomplete?: number;
  withSessionToken?: boolean;
  placesLibrary?: Ref<google.maps.PlacesLibrary | undefined>;
}

export default function usePlacesAutocomplete(
  query: Ref<string>,
  {
    placesLibrary,
    autocompletionRequest = {},
    debounce = 300,
    minLengthAutocomplete = 0,
    withSessionToken,
  }: GooglePlacesAutocompleteOptions = {}
) {
  interface State {
    sessionToken: google.maps.places.AutocompleteSessionToken | undefined;
    loading: boolean;
  }

  const state = reactive<State>({
    sessionToken: undefined,
    loading: false,
  });

  const suggestions = shallowRef<google.maps.places.PlacePrediction[]>([]);

  const fetchSuggestions = async () => {
    if (!placesLibrary?.value) {
      suggestions.value = [];
      return;
    }

    if (query.value.length < minLengthAutocomplete || !query.value.length) {
      suggestions.value = [];
      return;
    }

    state.loading = true;

    try {
      const request = autocompletionRequestBuilder(
        { ...autocompletionRequest },
        query.value,
        withSessionToken && state.sessionToken
      );
      const { suggestions: rawSuggestions } =
        await placesLibrary.value.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      suggestions.value = rawSuggestions
        .map((s) => s.placePrediction)
        .filter((p): p is google.maps.places.PlacePrediction => p !== null);
    } catch (e) {
      console.error("[usePlacesAutocomplete] error", e);
      suggestions.value = [];
    } finally {
      state.loading = false;
    }
  };

  const refreshSessionToken = () => {
    state.sessionToken = new google.maps.places.AutocompleteSessionToken();
  };

  const initPlaces = () => {
    if (!placesLibrary?.value) {
      console.error("[vue-use-places-autocomplete]: Google script not loaded");
      return;
    }

    refreshSessionToken();
    fetchSuggestions();
  };

  watchDebounced(query, fetchSuggestions, { debounce });

  return {
    ...toRefs(state),
    suggestions,
    refreshSessionToken,
    initPlaces,
  };
}
